import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import chevron from './icons/chevron.svg';
import { ActivityIcon } from './icons/teamQ/ActivityIcon';
import { AnalyticsIcon } from './icons/teamQ/AnalyticsIcon';
import { OverviewIcon } from './icons/teamQ/OverviewIcon';
import { RootState } from '../../store/store';
import { getCurrentPath } from '../utils';
import { UsersIcon } from './icons/account/UsersIcon';
import { TeamsIcon } from './icons/account/TeamsIcon';
import { OrganizationIcon } from './icons/account/OrganizationIcon';
import { AppsIcon } from './icons/account/AppsIcon';
import { BillingIcon } from './icons/account/BillingIcon';

export const Submenu: React.FC = () => {
  const { user, org } = useSelector((state: RootState) => state.auth);

  const { settings, status } = useSelector(
    (state: RootState) => state.org.webhook
  );

  const { pathname } = useLocation();

  const history = useHistory();

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const animationTimer = setTimeout(() => {
      if (!loaded) {
        setLoaded(true);
      }
    }, 100);

    return () => {
      clearTimeout(animationTimer);
    }; // eslint-disable-next-line
  }, []);

  const isNavigationDisabled =
    typeof org?.isFunctionalityEnabled === 'boolean' &&
    !org.isFunctionalityEnabled;

  const attrs = {
    wrapper: {
      className: `${loaded ? 'loaded ' : ''}submenu`,
    },

    header: {
      wrapper: {
        className: 'submenu__header',
      },

      title: {
        className: `${loaded ? 'loaded ' : ''}submenu__header-title`,
      },
    },

    body: {
      wrapper: {
        className: 'submenu__body',
      },

      link: {
        wrapper: {
          className: `${loaded ? 'loaded ' : ''} ${
            isNavigationDisabled ? 'disabled ' : ''
          }submenu__body__link`,
        },

        icon: {
          className: 'submenu__body__link-icon',
        },

        label: {
          className: 'submenu__body__link-label',
        },

        chevron: {
          className: 'submenu__body__link-chevron',
          src: chevron,
        },
      },
    },
  };

  const adminSettingsLinks = [
    {
      label: 'Users',
      Icon: UsersIcon,
      path: `/orgs/${user?.orgId}/admin-settings/users`,
    },
    {
      label: 'Teams',
      Icon: TeamsIcon,
      path: `/orgs/${user?.orgId}/admin-settings/teams`,
    },
    {
      label: 'Organization',
      Icon: OrganizationIcon,
      path: `/orgs/${user?.orgId}/admin-settings/organization`,
    },
    {
      label: 'Apps',
      Icon: AppsIcon,
      path: `/orgs/${user?.orgId}/admin-settings/apps`,
    },
    {
      label: 'Billing',
      Icon: BillingIcon,
      path: `/orgs/${user?.orgId}/admin-settings/billing`,
    },
  ];

  const teamQLinks = [
    {
      label: 'Overview',
      Icon: OverviewIcon,
      path: `/orgs/${user?.orgId}/users/${user?.userId}/team-analytics/overview`,
    },
    {
      label: 'Activity',
      Icon: ActivityIcon,
      path: `/orgs/${user?.orgId}/users/${user?.userId}/team-analytics/activity`,
    },
    {
      label: 'Analytics',
      Icon: AnalyticsIcon,
      path: `/orgs/${user?.orgId}/users/${user?.userId}/team-analytics/analytics`,
    },
  ];

  const goToPage = (path: string) => {
    if (pathname !== path) {
      history.push(path);
    }
  };

  // const appSumoPermissionCheck =
  //   org?.acquisitionSource &&
  //   org.acquisitionSource === 'AppSumo' &&
  //   user?.role === 'owner';

  const defineLabelClassName = (label: string) => {
    let result = 'submenu__body__link-label';

    if (
      label === 'Apps' &&
      (status.connectionLost ||
        status.updateFailed ||
        settings.isDisabledByError)
    ) {
      result = 'error submenu__body__link-label';
    }

    return result;
  };

  const mappedAdminSettingsLinks = adminSettingsLinks.map(
    ({ label, path, Icon }) =>
      path.includes('something') ? null : (
        // path.includes('billing') && !appSumoPermissionCheck ? null : (
        <div
          key={`${label}${path}`}
          className={`${loaded ? 'loaded ' : ''} ${
            isNavigationDisabled && !path.includes('admin-settings/billing')
              ? 'disabled '
              : ''
          }submenu__body__link`}
          onClick={() => goToPage(path)}
        >
          <div {...attrs.body.link.icon}>
            <Icon active={pathname === getCurrentPath(path, user)} />
          </div>
          <div className={defineLabelClassName(label)}>{label}</div>
          <img
            {...attrs.body.link.chevron}
            style={{ opacity: pathname === getCurrentPath(path, user) ? 1 : 0 }}
            alt=""
          />
        </div>
      )
  );

  const mappedTeamQLinks = teamQLinks.map(({ label, path, Icon }) => (
    <div
      key={`${label}${path}`}
      {...attrs.body.link.wrapper}
      onClick={() => goToPage(path)}
    >
      <div {...attrs.body.link.icon}>
        <Icon active={pathname === getCurrentPath(path, user)} />
      </div>
      <div {...attrs.body.link.label}>{label}</div>
      <img
        {...attrs.body.link.chevron}
        style={{ opacity: pathname === getCurrentPath(path, user) ? 1 : 0 }}
        alt=""
      />
    </div>
  ));

  const defineContentToShow = () => {
    let currentToShow: JSX.Element | null = null;

    if (pathname.includes('/admin-settings')) {
      currentToShow = (
        <div {...attrs.wrapper}>
          <div {...attrs.header.wrapper}>
            <div {...attrs.header.title}>Admin Settings</div>
          </div>
          <div {...attrs.body.wrapper}>{mappedAdminSettingsLinks}</div>
        </div>
      );
    } else if (pathname.includes('team-analytics/')) {
      currentToShow = (
        <div {...attrs.wrapper}>
          <div {...attrs.header.wrapper}>
            <div {...attrs.header.title}>Team Q</div>
          </div>
          <div {...attrs.body.wrapper}>{mappedTeamQLinks}</div>
        </div>
      );
    }

    return currentToShow;
  };

  const content = defineContentToShow();

  return content;
};
